import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, Stat, StatGrid } from '../components/Section';
import type { VulnFinding, ExposureFinding } from '../types';

export function VulnPage() {
  const scan = useScan();
  const v = scan.modules.vulns;
  const e = scan.modules.exposure;

  const exposed = e?.findings.filter(f => f.status === 'exposed') ?? [];
  const exposedBySev = bySeverity(exposed.map(f => f.severity));
  // Probes that timed out / were blocked are 'unknown' — NOT safe. We must not
  // claim "all clean" when some checks never completed.
  const inconclusive = e?.findings.filter(f => f.status === 'unknown').length ?? 0;

  const cveCounts = bySeverity(v?.vulnerabilities.map(x => x.severity) ?? []);
  const totalIssues = exposed.length + (v?.vulnerabilities.length ?? 0);

  return (
    <>
      <PageHeader
        title="Exposure & Vulnerability Checks"
        subtitle="Misconfiguration probes plus CVE matches against detected component versions"
      />

      <Section title="Overview" accent={totalIssues > 0 ? (exposedBySev.critical + cveCounts.critical > 0 ? 'rose' : 'amber') : 'green'}>
        <StatGrid cols={4}>
          <Stat label="Probes Run" value={e?.checked ?? 0} tone="info" sub="Misconfig & secret-leak checks" />
          <Stat label="Exposed" value={exposed.length}
                tone={exposed.length > 0 ? 'bad' : inconclusive > 0 ? 'warn' : 'ok'}
                sub={exposed.length > 0 ? 'Findings flagged as risk'
                   : inconclusive > 0 ? `${inconclusive} inconclusive`
                   : 'None found'} />
          <Stat label="CVE Queries" value={v?.queries_made ?? 0}
                tone="info" sub="Versioned packages checked" />
          <Stat label="CVE Matches" value={v?.vulnerabilities.length ?? 0}
                tone={v && v.vulnerabilities.length > 0 ? 'bad' : 'ok'}
                sub="OSV.dev hits on detected versions" />
        </StatGrid>
      </Section>

      {/* Empty-state when probes ran cleanly AND CVE matching had something to query */}
      {exposed.length === 0
       && (v?.vulnerabilities.length ?? 0) === 0
       && (v?.queries_made ?? 0) > 0 && (
        <Section accent={inconclusive > 0 ? 'amber' : 'green'}>
          <Banner tone={inconclusive > 0 ? 'warn' : 'ok'} title={inconclusive > 0 ? 'No exposures found (some probes inconclusive)' : 'Clean — no exposures or CVEs detected'}>
            No probed path returned an exposed artifact, and CVE matching found nothing
            affecting the detected versions.
            {inconclusive > 0 && ` ${inconclusive} probe(s) were inconclusive (timed out or blocked) and can't be confirmed safe.`}
          </Banner>
        </Section>
      )}

      {/* Probes ran cleanly but CVE side had no versioned tech to query */}
      {exposed.length === 0
       && (v?.vulnerabilities.length ?? 0) === 0
       && (v?.queries_made ?? 0) === 0 && (
        <Section accent={inconclusive > 0 ? 'amber' : 'green'}>
          <Banner tone={inconclusive > 0 ? 'warn' : 'ok'} title={inconclusive > 0 ? 'No exposures found (some probes inconclusive)' : 'Exposure probes clean'}>
            No probed path returned an exposed artifact. We didn't find any
            version-bearing JavaScript packages to run CVE matching against —
            common for SPAs and CDN-fronted sites.
            {inconclusive > 0 && ` ${inconclusive} probe(s) were inconclusive (timed out or blocked) and can't be confirmed safe.`}
          </Banner>
        </Section>
      )}

      {/* Exposure findings, grouped by category */}
      {exposed.length > 0 && (
        <Section
          title={`Exposure Findings (${exposed.length})`}
          accent={exposedBySev.critical > 0 ? 'rose' : exposedBySev.high > 0 ? 'rose' : 'amber'}
          status={{
            label: exposedBySev.critical > 0 ? `${exposedBySev.critical} CRITICAL` :
                   exposedBySev.high > 0 ? `${exposedBySev.high} HIGH` :
                   exposedBySev.medium > 0 ? `${exposedBySev.medium} MEDIUM` :
                   exposedBySev.low > 0 ? `${exposedBySev.low} LOW` : 'INFO',
            tone: exposedBySev.critical + exposedBySev.high > 0 ? 'bad' :
                  exposedBySev.medium > 0 ? 'warn' : 'info'
          }}>
          <ExposureGroups findings={exposed} />
        </Section>
      )}

      {/* CVE list */}
      {v && v.vulnerabilities.length > 0 && (
        <Section
          title={`CVE Matches (${v.vulnerabilities.length})`}
          accent={cveCounts.critical + cveCounts.high > 0 ? 'rose' : 'amber'}
          status={{
            label: cveCounts.critical > 0 ? `${cveCounts.critical} CRITICAL` :
                   cveCounts.high > 0 ? `${cveCounts.high} HIGH` :
                   cveCounts.medium > 0 ? `${cveCounts.medium} MEDIUM` :
                   cveCounts.low > 0 ? `${cveCounts.low} LOW` : 'INFO',
            tone: cveCounts.critical + cveCounts.high > 0 ? 'bad' :
                  cveCounts.medium > 0 ? 'warn' : 'info'
          }}>
          <ul className="space-y-2.5">
            {v.vulnerabilities.map(f => <CveRow key={f.cve_id} f={f} />)}
          </ul>
        </Section>
      )}

      {/* Components needing manual review */}
      {v && v.unmapped.length > 0 && (
        <Section title="Manual review required" accent="slate">
          <p className="text-sm text-ink-2">
            These components were fingerprinted with versions but don't have
            first-party CVE coverage in OSV.dev. Cross-reference vendor advisories
            directly:
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {v.unmapped.map(u => (
              <code key={u} className="rounded-sm bg-panel-2 px-2 py-1 text-xs">{u}</code>
            ))}
          </div>
        </Section>
      )}

      {/* Coverage hint when CVE check had nothing to query but exposures DID
          fire — clarifies for the user why the CVE side is empty. */}
      {v && v.queries_made === 0 && exposed.length > 0 && (
        <Section accent="slate">
          <Banner tone="info" title="No version-bearing components detected for CVE matching">
            We couldn't fingerprint a versioned JavaScript package on this site,
            so the CVE database wasn't queried. The exposure findings above are
            independent of CVE coverage.
          </Banner>
        </Section>
      )}
    </>
  );
}

function ExposureGroups({ findings }: { findings: ExposureFinding[] }) {
  const groups = findings.reduce<Record<string, ExposureFinding[]>>((acc, f) => {
    (acc[f.category] = acc[f.category] ?? []).push(f);
    return acc;
  }, {});
  const order = ['Source-Code Exposure', 'Admin Surface', 'Information Disclosure', 'CORS', 'Transport'];
  const ordered = order.filter(cat => groups[cat]?.length).map(cat => [cat, groups[cat]] as const);
  return (
    <div className="space-y-4">
      {ordered.map(([category, items]) => (
        <div key={category}>
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-3">{category}</p>
          <ul className="space-y-2">
            {items.map(f => <ExposureRow key={f.id} f={f} />)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ExposureRow({ f }: { f: ExposureFinding }) {
  const sevPill = {
    critical: 'pill-bad',
    high:     'pill-bad',
    medium:   'pill-warn',
    low:      'pill-info',
    info:     'pill-mute'
  }[f.severity];
  return (
    <li className="rounded-lg border border-line bg-panel p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`pill ${sevPill}`}>{f.severity.toUpperCase()}</span>
        <span className="font-semibold text-ink">{f.title}</span>
        {f.evidence && (
          <span className="font-mono text-[11px] text-ink-3">{f.evidence}</span>
        )}
        {f.url && (
          <a href={f.url} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] text-accent hover:text-sentinel-900">open ↗</a>
        )}
      </div>
      <p className="mt-1.5 leading-relaxed text-ink-2">{f.description}</p>
    </li>
  );
}

function CveRow({ f }: { f: VulnFinding }) {
  const sevPill = {
    critical: 'pill-bad',
    high:     'pill-bad',
    medium:   'pill-warn',
    low:      'pill-info',
    undefined: 'pill-mute'
  }[f.severity ?? 'undefined'];
  return (
    <li className="rounded-lg border border-line bg-panel p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`pill ${sevPill}`}>
          {(f.severity ?? 'unknown').toUpperCase()}
        </span>
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-xs font-semibold text-accent hover:text-sentinel-900"
        >{f.cve_id} ↗</a>
        <span className="text-xs text-ink-3">
          {f.package_name}@{f.package_version}
        </span>
        {f.published && (
          <span className="text-[11px] text-ink-3">· published {f.published.slice(0, 10)}</span>
        )}
      </div>
      <p className="mt-1.5 leading-relaxed text-ink-2">{f.summary}</p>
    </li>
  );
}

function bySeverity(severities: (string | undefined)[]) {
  const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0, none: 0 };
  for (const s of severities) {
    if      (s === 'critical') c.critical++;
    else if (s === 'high')     c.high++;
    else if (s === 'medium')   c.medium++;
    else if (s === 'low')      c.low++;
    else if (s === 'info')     c.info++;
    else                       c.none++;
  }
  return c;
}
